/* eslint-disable react-hooks/exhaustive-deps */
import { JsonObject, RoleFunction } from '@eagle/common';
import { Person, PersonThingRangeResponse, PersonType, Thing } from '@eagle/core-data-types';
import { Alert, Button, Card, CardContent, Stack, styled, TextField, Typography } from '@ethings/ui-toolkit';
import { isEqual } from 'lodash';
import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import validator from 'validator';
import { useAuthenticated } from '../../auth';
import { PHONE_MIN_LENGTH, T_ONE } from '../../constants';
import { usePromise } from '../../hooks';
import { CacheDataTypes, Portal, Portals, PortalTypes } from '../../types';
import { testid, useHasAuthorization } from '../../util';
import { isValidMobilePhoneValue } from '../../util/validation';
import { AssignPersonDialog } from '../assign-dialog';
import { BasicEntityCard } from '../basic-card/basic-card';
import { EntityCardProps, EntityDefaultCardProps, EntityEditableCard } from '../basic-card/types';
import { ErrorMessage } from '../error-message';
import { FetchOneOfAll } from '../fetch';
import { Icon } from '../icons';
import { LabelsEditor } from '../labels-editor';
import { LoadingButton } from '../loading-button';
import { MiddleSpinner } from '../middle-spinner';
import { PortalSwitchingDropdown } from '../portal-switching';
import { PropertiesElement } from '../properties-element';
import { ResponsiveText } from '../responsive-text';
import { TagsEditor } from '../tags-editor';
import { TextEditor } from '../text-editor';
import { ThingPersonDetail } from '../thing-card/thing-person-detail';

interface ThingPersonProps {
  'data-testid'?: string;
  portal: Portal;
  readonly?: boolean;
  thing: Thing;
  title?: string;
}

const PERSON_ADMIN = [RoleFunction.PERSON_ADMINISTRATOR] as const;
const ASSIGN_PERSON_ROLES = [RoleFunction.LINKING_ADMINISTRATOR] as const;

export const ThingPersonCard: FC<ThingPersonProps> = ({ thing, portal, readonly, title, ...props }) => {
  const { axios, restClient } = useAuthenticated();
  const { t } = useTranslation(['common', 'terms']);
  const { hasAuthorization } = useHasAuthorization();
  const assignThingsPermissions = hasAuthorization(ASSIGN_PERSON_ROLES);

  const [currentThingPersons, setCurrentThingPersons] = useState<PersonThingRangeResponse[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAssignPersonDialog, setShowAssignPersonDialog] = useState(false);
  const [updatePersons, setUpdatePersons] = useState(false);

  const [thingPersons, thingPersonsError, thingPersonsState] = usePromise<PersonThingRangeResponse[]>(
    async () => {
      if (!thing) return Promise.resolve([]);
      return restClient.personThing.getCurrentRangesByThing(thing._id);
    },
    [axios, thing, updatePersons],
  );

  useEffect(() => {
    if (thingPersons) setCurrentThingPersons(thingPersons);
  }, [thingPersons]);

  const updateCurrentPersons = (i: number): void => {
    const temporaryPersons = [...currentThingPersons];
    temporaryPersons.splice(i, 1);
    setCurrentThingPersons(temporaryPersons);
  };

  if (thingPersonsError) return <ErrorMessage error={thingPersonsError} />;
  if (thingPersonsState === 'pending') return <MiddleSpinner />;
  if (!thing) return <></>;

  return (
    <Card data-testid={props['data-testid']} style={{ height: 'min-content' }}>
      <PortalSwitchingDropdown
        actions={
          <>
            {assignThingsPermissions
              && (() => {
                return (<Button
                  disabled={readonly}
                  onClick={() => setShowAssignPersonDialog(true)}
                  size="small"
                  startIcon={<Icon icon="Link" />}
                >
                  {!currentThingPersons.length ? t('common:page.thing-detail.person-list.assign.action') : t('common:page.thing-detail.person-list.assign-another.action')}
                </Button>);
              })()
            }
          </>
        }
        entity={currentThingPersons[0]}
        excludePortals={[Portals.MANAGE]}
        portal={portal}
        title={title ?? t('terms:person', { count: !currentThingPersons.length ? T_ONE : currentThingPersons.length })}
        type={PortalTypes.THING_PERSON_TYPE}
      />
      <CardContent>
        {!currentThingPersons.length
          ? <>
            <Typography mb={1.5} color="text.secondary" fontStyle="italic" variant="body2">
              {t('common:page.thing-detail.person-list.hint.none')}
            </Typography>
            {assignThingsPermissions
              && (() => {
                return (<Button
                  disabled={readonly}
                  onClick={() => setShowAssignPersonDialog(true)}
                  size="small"
                  startIcon={<Icon icon="Link" />}
                >
                  {t('common:page.thing-detail.person-list.assign.action')}
                </Button>);
              })()
            }
          </>
          : <>
            {currentThingPersons.map((thingPerson, i) => {
              return (i === 0 || isExpanded)
                && <Fragment key={i}>
                  <ThingPersonDetail
                    canEditAssignment={assignThingsPermissions}
                    finishPerson={() => updateCurrentPersons(i)}
                    readonly={readonly}
                    thing={thing}
                    thingPerson={thingPerson}
                  />
                </Fragment>;
            })}
            {(currentThingPersons.length > 1)
              && (() => {
                const ToggleButton = styled(Button)({ padding: 0 });
                return (<ToggleButton
                  onClick={() => setIsExpanded(!isExpanded)}
                  size="small"
                >
                  {isExpanded ? t('common:common.action.hideOthers') : t('common:common.labels.othersWithCount', { count: currentThingPersons.length - 1 })}
                </ToggleButton>);
              })()
            }
          </>
        }
      </CardContent>
      <AssignPersonDialog
        assignedPersons={currentThingPersons.map((thingPerson) => ({ personId: thingPerson.personId, role: thingPerson.role }))}
        handleClose={() => {
          setShowAssignPersonDialog(false);
          setUpdatePersons(!updatePersons);
        }}
        open={showAssignPersonDialog}
        tFunction={t}
        thing={thing}
      />
    </Card>
  );
};

const NormalCard: FC<EntityDefaultCardProps<Person, PersonType>> = ({ entity, entityType, portal, ...props }) => {
  if (entityType) {
    return <BasicEntityCard
      data-testid={props['data-testid']}
      entity={entity}
      entityType={entityType}
      excludePortals={[Portals.MANAGE]}
      portal={portal}
      type={PortalTypes.PERSON_TYPE}
    />;
  }

  return (
    <FetchOneOfAll
      dataType={CacheDataTypes.PERSON_TYPE}
      id={entity.personTypeId}
      renderFactory={(data: PersonType) => (
        <BasicEntityCard
          data-testid={props['data-testid']}
          entity={entity}
          entityType={data}
          excludePortals={[Portals.MANAGE]}
          portal={portal}
          type={PortalTypes.PERSON_TYPE}
        />
      )}
    />
  );
};

const EditableCard: FC<EntityEditableCard<Person, PersonType>> = ({
  disableSave,
  entityState,
  entityType,
  error,
  handlePropertyChange,
  loadingButtonClass,
  readonly,
  saveInProgress,
  updateEntity,
}) => {
  const { t } = useTranslation(['common']);
  const { hasAuthorization } = useHasAuthorization();
  const hasEditPermissions = hasAuthorization(PERSON_ADMIN);
  const fieldErrorsRef = useRef<Record<string, boolean>>({});
  const [hasLabelErrors, setHasLabelErrors] = useState(false);

  const labelText = t('common:terms.label');

  const isDisplayValid = useMemo(() => {
    return entityState.display.trim().length > 0;
  }, [entityState.display]);

  const handleValidation = useCallback((field: string, valid: boolean): void => {
    fieldErrorsRef.current = { ...fieldErrorsRef.current, [field]: !valid };
  }, []);

  const isFieldsValid = !Object.values(fieldErrorsRef.current).some(Boolean) && !hasLabelErrors && isDisplayValid;
  const prevPropertiesRef = useRef<JsonObject>({});

  const propertiesElement = useMemo(() => {
    if (!entityType.properties) return null;

    const handlePropertiesChange = (properties: JsonObject): void => {
      if (!properties || typeof properties !== 'object') return;

      if (isEqual(properties, prevPropertiesRef.current)) return;
      prevPropertiesRef.current = properties;

      Object.entries(properties).forEach(([propertyName, propertyValue]) => {
        const propDef = entityType.properties.definitions[propertyName];

        if (!propDef || !propertyValue || typeof propertyValue !== 'object' || !('value' in propertyValue)) return;

        const stringValue = String(propertyValue.value || '');
        let isValid = true;

        if (propDef.type === 'phone') {
          isValid = stringValue === '' || (stringValue.length > PHONE_MIN_LENGTH && isValidMobilePhoneValue(stringValue));
        } else if (propDef.type === 'email' || (propDef.type === 'text' && propDef.label.toLocaleLowerCase() === 'email')) {
          isValid = stringValue === '' || validator.isEmail(stringValue);
        }
        handleValidation(propertyName, isValid);
      });

      void handlePropertyChange('properties')(properties);
    };

    return (
      <PropertiesElement
        disabled={!hasEditPermissions || saveInProgress || readonly}
        onChange={handlePropertiesChange}
        handleValidation={handleValidation}
        mobileOnly={true}
        properties={entityState.properties}
        propertyDefinitions={entityType.properties}
        size="small"
      />
    );
  }, [
    entityType.properties,
    hasEditPermissions,
    saveInProgress,
    readonly,
    handlePropertyChange,
    handleValidation,
    entityState.properties,
  ]);

  return (
    <Stack spacing={2.5}>
      <TextField
        disabled
        label={t('common:common.labels.id')}
        size="small"
        value={entityState._id}
        data-testid={testid`person-id-${entityState._id}`}
      />
      <TextEditor
        disabled={!hasEditPermissions || saveInProgress || readonly}
        label={t('common:common.labels.display')}
        onKeyUp={handlePropertyChange('display')}
        required
        size="small"
        value={entityState.display}
        data-testid="person-display-input"
      />
      {propertiesElement}
      <TagsEditor
        disabled={!hasEditPermissions || saveInProgress || readonly}
        label={t('common:terms.tag')}
        onChange={handlePropertyChange('tags')}
        placeholder={t('common:common.hint.tag')}
        size="small"
        tags={entityState.tags}
      />
      <ResponsiveText
        variant="h6"
        responsiveOptions={{
          minFontSize: '1rem',
          maxFontSize: '1.25rem',
          textLengthThreshold: 20,
        }}
      >
        {labelText}
      </ResponsiveText>
      <LabelsEditor
        disabled={!hasEditPermissions || saveInProgress || readonly}
        labels={entityState.labels}
        onChange={handlePropertyChange('labels')}
        onValidationChange={setHasLabelErrors}
        placeholder={t('common:common.hint.label')}
        size="small"
        textfieldLabel={t('common:terms.label')}
      />
      {error
        && <Alert severity="error">
          {error}
        </Alert>
      }
      {hasEditPermissions
        && <LoadingButton
          className={loadingButtonClass}
          disabled={readonly || disableSave || !isFieldsValid}
          loadingCaption={t('common:common.hint.saving')}
          task={updateEntity}
          sx={{ alignSelf: 'end', minWidth: 120 }}
          data-testid="save-button"
        >
          {t('common:common.action.save')}
        </LoadingButton>
      }
    </Stack>
  );
};

const AdminCard: FC<EntityCardProps<Person, PersonType>> = ({ entityType, ...props }) => {
  if (!entityType) {
    return (
      <FetchOneOfAll
        dataType={CacheDataTypes.PERSON_TYPE}
        id={props.entity.personTypeId}
        renderFactory={(data: PersonType) => <EditableCard {...props as Required<EntityEditableCard<Person, PersonType>>} entityType={data} />}
      />
    );
  }
  return <EditableCard {...props as Required<EntityEditableCard<Person, PersonType>>} entityType={entityType} />;
};

export const PersonCard: FC<EntityCardProps<Person, PersonType>> = ({ portal, editable, ...props }) => {
  if (!editable) return <NormalCard {...props} portal={portal} />;
  return (
    <Card data-testid={props['data-testid']}>
      <PortalSwitchingDropdown
        entity={props.entity}
        excludePortals={[Portals.MANAGE]}
        portal={portal}
        type={PortalTypes.PERSON_TYPE}
        data-testid={'person-title'}
      />
      <CardContent>
        <AdminCard {...props as EntityEditableCard<Person, PersonType>} />
      </CardContent>
    </Card>
  );
};
